import type { GetStaticPropsContext } from 'next';

import { Flex, Heading, List, ListIcon, ListItem } from '@chakra-ui/react';
import { CheckedIcon } from '@udacity/chakra-uds-icons';

import { BlueWaveHero } from '~/components/blue-wave-hero';
import { Card } from '~/components/card';
import { ContentContainer } from '~/components/container';
import { Markdown } from '~/components/markdown';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';

const pageData = {
  pageTitle: 'Technology Requirements',
  requirementGroups: [
    {
      title: 'Operating System',
      requirements: [
        '**PC:** Windows 7 or higher with the latest updates installed (note: Internet Explorer is not supported).',
        '**Mac:** OS X 10.11 or higher with latest updates installed.',
        '**Linux:** Any recent distribution that has the supported browsers installed.',
        '**Ubuntu:** 17.10+ or 14.04 LTS+.',
      ],
      footerMessage: undefined,
    },
    {
      title: 'Browser',
      requirements: [
        'An up-to-date version of Chrome is strongly recommended. We also support the latest versions of Firefox, Safari, Opera and Microsoft Edge.',
        'At this time, most features, including Classroom, Knowledge, and Student Hub, are supported on mobile web.',
      ],
      footerMessage: undefined,
    },
    {
      title: 'Hardware',
      requirements: [
        'Each course and program has its own specific hardware requirements in terms of processing power, hard drive space and any external hardware.',
      ],
      footerMessage: "**Please refer to the program's individual overview page for additional info.**",
    },
    {
      title: 'Internet Connectivity',
      requirements: [
        '**Download:** 2+ Mbps recommended; minimum 1 Mbps.',
        '**Upload:** 0.5 Mbps. Mobile Data: Reliable Wi-Fi or cellular data connection.',
      ],
      footerMessage: undefined,
    },
    {
      title: 'Youtube',
      requirements: [
        '**Ability to watch YouTube videos is required.** If YouTube is blocked on your network, please contact your IT support or internet provider. If you do not use the HTML5 player, we recommend Adobe Flash Player v28.',
      ],
      footerMessage: undefined,
    },
    {
      title: 'Google Account',
      requirements: [
        'Required for Webcasts, one on one appointments, and exit interviews. Sign up for Google account [here](https://accounts.google.com/signup/v2/createaccount?flowName=GlifWebSignIn&flowEntry=SignUp). If you have a Gmail or YouTube account, that same login can be used to access all of Google’s features.',
      ],
      footerMessage: undefined,
    },
  ],
} as const;

export default function TechRequirements({ page }: { page: Page }) {
  return (
    <>
      <PageHead page={page} />
      <PageLayout withoutVerticalPadding>
        <BlueWaveHero>
          <Heading as='h1' size='h1' textAlign='center'>
            {pageData.pageTitle}
          </Heading>
        </BlueWaveHero>
        <ContentContainer
          padding={{
            base: '40px 16px 80px',
            md: '80px 16px 120px',
            xl: '80px 0px 120px',
          }}
        >
          <List
            display='grid'
            gridGap={{
              base: '24px',
              md: '32px',
            }}
            gridTemplateColumns={{
              base: '1fr',
              md: '1fr 1fr',
            }}
          >
            {pageData.requirementGroups.map((requirementGroup) => (
              <ListItem key={requirementGroup.title} display='flex'>
                <Card
                  as='article'
                  border='none'
                  boxShadow='0px 4px 6px -2px rgba(11, 11, 11, 0.05), 0px 10px 15px -3px rgba(11, 11, 11, 0.10);'
                  padding={{
                    base: '24px 16px',
                    md: '40px',
                  }}
                >
                  <Heading as='h2' marginBottom='16px' size='h2'>
                    {requirementGroup.title}
                  </Heading>
                  <Flex flexDirection='column' gap='40px'>
                    {requirementGroup.requirements.map((requirement) => (
                      <>
                        <List key={requirement} display='flex' flexDirection='column' gap='20px'>
                          <ListItem key={requirement} alignItems='center' display='flex'>
                            <ListIcon
                              alignSelf='flex-start'
                              as={CheckedIcon}
                              color='blue.500'
                              height='32px'
                              width='32px'
                            />
                            <Markdown markdownText={requirement} />
                          </ListItem>
                        </List>
                        {requirementGroup.footerMessage && (
                          <Markdown
                            key={requirementGroup.footerMessage}
                            markdownText={requirementGroup.footerMessage}
                          />
                        )}
                      </>
                    ))}
                  </Flex>
                </Card>
              </ListItem>
            ))}
          </List>
        </ContentContainer>
      </PageLayout>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  const page = await getPage('/tech-requirements');

  return {
    props: {
      page,
    },
  };
}
